import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useQuizData } from '../../hooks/useQuizData';
import { useGlobalSettings } from '../../hooks/useGlobalSettings';
import { useAvatar } from '../../hooks/useAvatar';
import Animation from '../common/Animation';
import QuizButton from '../common/QuizButton';
import QuizBackButton from '../common/QuizBackButton';
import { motion } from 'framer-motion';

export default function QuizPage({ difficulty, theme }) {
  const [index, setIndex]   = useState(0);
  const [chosen, setChosen] = useState(null);
  const [showFB, setShowFB] = useState(false);

  const { questions: quiz, loading: qLoad, error: qErr } = useQuizData(difficulty);
  const { globalSettings, loading: gLoad, error: gErr }  = useGlobalSettings();
  const { avatarData, loading: aLoad, error: aErr }      = useAvatar('Startseite');

  const nav = useNavigate();

  const IDLE_MS = 120_000;
  const inactivityRef = useRef(null);

  useEffect(() => {
    const resetTimer = () => {
      clearTimeout(inactivityRef.current);
      inactivityRef.current = setTimeout(() => {
        nav('/quiz');
      }, IDLE_MS);
    };

    resetTimer();
    const onKeydown = () => resetTimer();
    const onClick   = () => resetTimer();
    const onPointer = () => resetTimer();

    window.addEventListener('keydown', onKeydown);
    window.addEventListener('click', onClick);
    window.addEventListener('pointerdown', onPointer);

    return () => {
      window.removeEventListener('keydown', onKeydown);
      window.removeEventListener('click', onClick);
      window.removeEventListener('pointerdown', onPointer);
      clearTimeout(inactivityRef.current);
    };
  }, [nav]);

  const loaded    = !qLoad && !gLoad && !aLoad && !qErr && !gErr && !aErr && quiz.length;
  const question  = loaded ? quiz[index] : null;
  const logoUrl   = globalSettings?.logoUrl;
  const avatarURL = avatarData?.[0]?.animation;

  const handleClick = useCallback(
    idx => {
      if (!loaded || showFB) return;
      setChosen(idx);
      setShowFB(true);
      const ms = ((quiz[index]?.feedbackDuration) ?? 10) * 1000;
      setTimeout(() => {
        if (index + 1 < quiz.length) {
          setIndex(p => p + 1);
          setChosen(null);
          setShowFB(false);
        } else {
          nav('/quiz/startseite');
        }
      }, ms);
    },
    [loaded, showFB, index, quiz, nav],
  );

  if (qLoad || gLoad || aLoad) return null;
  if (qErr || gErr || aErr) return <p>Fehler beim Laden der Daten.</p>;
  if (!quiz.length) return <p>Keine Quiz-Fragen vorhanden.</p>;

  const feedbackSeconds = (question?.feedbackDuration ?? 10);

  return (
    <div
      className="min-h-screen relative flex"
      style={{ backgroundColor: theme.backgroundColor, color: theme.textColor }}
    >
      {/* Logo */}
      <img src={logoUrl} alt="Logo" className="absolute top-16 left-16 h-16" />

      {/* Zurück-Button */}
      <div className="absolute top-12 right-20 z-20 pointer-events-auto">
        <QuizBackButton
          text="Zurück"
          backgroundColor={theme.foregroundColor}
          textColor={theme.textColor}
          onClick={() => nav('/quiz/startseite')}
        />
      </div>

      {/* Avatar */}
      <div className="absolute bottom-0 transform -translate-x-80 z-0 pointer-events-none">
        <Animation
          src={avatarURL}
          className="object-contain portrait:h-full portrait:w-auto landscape:min-w-fit pointer-events-none"
        />
      </div>

      {/* Quiz-Box */}
      <div className="flex ml-auto mr-24 my-auto w-[65%]">
        <div
          className="w-full rounded-tl-[150px] rounded-br-[150px] shadow-xl flex flex-col items-center px-12 py-16 z-10"
          style={{ backgroundColor: theme.foregroundColor }}
        >
          <ReactMarkdown className="text-4xl md:text-5xl leading-tight text-center uppercase mb-6 pointer-events-none">
            {showFB
              ? question.answers[chosen]?.isCorrect
                ? question.correctText
                : question.wrongText
              : question.text}
          </ReactMarkdown>

          {/* Countdown-Balken */}
          {showFB && (
            <div className="w-full mb-6">
              <div className="relative w-full h-2 rounded overflow-hidden">
                {/* Track */}
                <div
                  className="absolute inset-0 rounded"
                  style={{ backgroundColor: theme.textColor, opacity: 0.25 }}
                />
                {/* Füllstand */}
                <motion.div
                  key={`${index}-${chosen}`}
                  className="absolute inset-0 rounded"
                  style={{ backgroundColor: theme.textColor, transformOrigin: 'left center' }}
                  initial={{ scaleX: 1 }}
                  animate={{ scaleX: 0 }}
                  transition={{ duration: feedbackSeconds, ease: 'linear' }}
                />
              </div>
            </div>
          )}

          {!showFB && (
            <div className="grid grid-cols-2 gap-6 w-full pointer-events-auto">
              {question.answers.map((ans, idx) => (
                <QuizButton
                  key={idx}
                  text={ans.label}
                  theme={theme}
                  handleClick={() => handleClick(idx)}
                />
              ))}
            </div>
          )}

          <div className="flex gap-4 mt-12">
            {quiz.map((_, i) => (
              <span
                key={i}
                className={`text-2xl ${i === index ? 'font-bold' : 'opacity-40'}`}
              >
                {i + 1}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}